/* -----------------Globals--------------- */
import { useEffect, useState } from "react";

/* -----------------Child components--------------- */
import StudySessionView from "../components/StudySessionView";

import { useLocalSearchParams } from "expo-router";
/* -----------------Helpers & Hooks--------------- */
import useSessionStore from "../hooks/useSessionStore";

import { IS_DEV } from "@constants/env";
import { useAuthContext } from "@features/auth/contexts/AuthContext";
import { replayManager } from "@helpers/lib/sentry";
import { useAppStore } from "@hooks/useAppStore";
import { useGlobalStore } from "@hooks/useGlobalStore";
import Loader from "@memorang/ui/components/Loader";
import MoreItemsAlertDialog from "@memorang/ui/components/MoreItemsAlertDialog";
import { useDevice } from "@memorang/ui/useDevice";
import { AudioPlaybackTestDialog } from "../components/AudioPlaybackTestDialog";
import PracticeSessionView from "../components/PracticeSessionView";
import SessionDisclaimerDialog from "../components/dialogs/StudySessionDisclaimerDialog";
import { useAilaSessionSync } from "../hooks/useAilaSessionSync";
import useCurrentSession from "../hooks/useCurrentSession";
import useInterruptAudioOnItemChange from "../hooks/useInterruptAudioOnItemChange";
/* -----------------Types--------------- */
import { useResumeSession } from "../hooks/useResumeSession";

const SessionContainer = () => {
	useAilaSessionSync();
	useInterruptAudioOnItemChange();
	const numItemsRequested = useSessionStore((state) => state.numItemsRequested);
	const sessionId = useSessionStore((state) => state.id);

	const showMoreItemsAlert = useSessionStore(
		(state) => state.showMoreItemsAlert,
	);
	const isSummativeTest = useSessionStore((state) => state.isSummativeTest);
	const totalTime = useSessionStore((state) => state.totalTime);
	const inReviewMode = useSessionStore((state) => state.inReviewMode);
	const sessionSubType = useSessionStore((state) => state.sessionSubType);

	const enableSessionReplay = useAppStore(
		(state) => state.sessionView?.enableSessionReplay,
	);

	const shouldEnableSessionReplay =
		enableSessionReplay && isSummativeTest && !inReviewMode && !IS_DEV;

	useEffect(() => {
		if (shouldEnableSessionReplay) {
			replayManager().startReplay();
		} else {
			replayManager().stopReplay();
		}
	}, [shouldEnableSessionReplay]);

	const disclaimer = useAppStore((state) => state?.sessionView?.disclaimer);

	const [showDisclaimer, setShowDisclaimer] = useState(disclaimer != null);

	const handleCloseDisclaimer = () => {
		setShowDisclaimer(false);
	};

	const {
		currentBlock,
		currentBlockIndex,
		numSessionItems: numSections,
		numTotalItems,
		hasListeningItems,
	} = useCurrentSession();

	const { isMobile } = useDevice();

	const toggleShowMoreItemsAlert = useSessionStore(
		(state) => state.toggleShowMoreItemsAlert,
	);

	const deviceRecommendationDialog = useAppStore(
		(state) => state.exam?.practiceTest?.deviceRecommendationDialog,
	);
	const shouldShowDeviceRecommendationDialog =
		deviceRecommendationDialog?.enabled && isMobile;

	const [showDeviceRecommendationDialog, setShowDeviceRecommendationDialog] =
		useState(shouldShowDeviceRecommendationDialog);

	const handleCloseDeviceRecommendationDialog = () => {
		setShowDeviceRecommendationDialog(false);
	};

	const { handleResumeSession } = useResumeSession();
	const { userLogicalId } = useAuthContext();

	const showAudioTestDialog = useAppStore(
		(state) => state.app?.accountSettings?.showAudioTestDialog,
	);

	const showAudioPlaybackTestDialogGlobal = useGlobalStore(
		(state) => state.showAudioPlaybackTestDialog,
	);
	const [showAudioPlaybackTestDialog, setShowAudioPlaybackTestDialog] =
		useState(showAudioPlaybackTestDialogGlobal && showAudioTestDialog);

	const id = useLocalSearchParams<{
		id: string;
	}>().id;

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		if (!sessionId && userLogicalId && id) {
			handleResumeSession(Number(id), { replaceRoute: true });
		}
	}, [id, sessionId, userLogicalId]);

	if (!sessionId) {
		return <Loader />;
	}

	if (isSummativeTest) {
		return (
			<>
				<PracticeSessionView
					sessionItems={currentBlock}
					sessionId={sessionId}
					currentBlockIndex={currentBlockIndex}
					totalTime={totalTime}
					showDisclaimer={showDisclaimer || showDeviceRecommendationDialog}
					inReviewMode={inReviewMode}
					reportSessionType={sessionSubType}
					numSections={numSections}
					numTotalItems={numTotalItems}
				/>
				{showDisclaimer && disclaimer && !inReviewMode && (
					<SessionDisclaimerDialog
						visible={showDisclaimer}
						handleClose={handleCloseDisclaimer}
						text={disclaimer}
						title={"Disclaimer"}
					/>
				)}
				{showDeviceRecommendationDialog && (
					<SessionDisclaimerDialog
						visible={showDeviceRecommendationDialog}
						handleClose={handleCloseDeviceRecommendationDialog}
						text={deviceRecommendationDialog?.message}
						title={deviceRecommendationDialog?.title}
					/>
				)}
			</>
		);
	}

	return (
		<>
			<StudySessionView
				sessionItems={currentBlock}
				showTimerPausingDialog={Boolean(
					(hasListeningItems && showAudioPlaybackTestDialog) ||
						(showDisclaimer && disclaimer && !inReviewMode) ||
						showMoreItemsAlert,
				)}
			/>
			{showMoreItemsAlert && (
				<MoreItemsAlertDialog
					visible={showMoreItemsAlert}
					handleClose={toggleShowMoreItemsAlert}
					numItemsAvailable={currentBlock.length}
					numItemsRequested={numItemsRequested}
				/>
			)}
			{hasListeningItems && showAudioPlaybackTestDialog && (
				<AudioPlaybackTestDialog
					showDialog={true}
					handleClose={() => {
						setShowAudioPlaybackTestDialog(false);
					}}
				/>
			)}
			{showDisclaimer && disclaimer && !inReviewMode && (
				<SessionDisclaimerDialog
					visible={showDisclaimer}
					handleClose={handleCloseDisclaimer}
					text={disclaimer}
					title={"Disclaimer"}
				/>
			)}
		</>
	);
};

export default SessionContainer;
